// ============================================================
//  htmlExport — renders Craft.js serialized JSON → standalone HTML
//  Uses ReactDOMServer.renderToStaticMarkup on the deserialized tree
// ============================================================

import { useEffect, useState } from 'react';
import type { HeaderState, PageId } from '../types/index';
import { PAGE_LABELS, PAGES } from '../types/index';

// ── Block HTML renderers (mirrors UserBlocks visual output) ──
function esc(s: unknown): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface NodeData {
  type: { resolvedName: string };
  props: Record<string, unknown>;
  nodes: string[];
  linkedNodes: Record<string, string>;
}

function renderNode(nodes: Record<string, NodeData>, nodeId: string): string {
  const node = nodes[nodeId];
  if (!node) return '';

  const p = node.props;
  const type = node.type?.resolvedName || '';

  console.log('Rendering node', nodeId, type, p);

  // Container wrapper style
  const wrapStyle = [
    p.backgroundColor ? `background:${String(p.backgroundColor)}` : '',
    p.color ? `color:${String(p.color)}` : '',
    p.margin ? `margin:${String(p.margin)}` : '',
    p.borderRadius ? `border-radius:${String(p.borderRadius)}` : '',
    p.border ? `border:${String(p.border)}` : '',
  ]
    .filter(Boolean)
    .join(';');

  const wrap = (inner: string): string =>
    wrapStyle ? `<div style="${wrapStyle}">${inner}</div>` : inner;
  const align = p.textAlign
    ? `text-align:${String(p.textAlign)}`
    : p.align
    ? `text-align:${String(p.align)}`
    : '';
  const pad = p.padding ? `padding:${String(p.padding)}` : '';

  switch (type) {
    case 'Titre': {
      const tag = (p.level as string) || 'h2';
      return wrap(
        `<div style="padding:24px 32px;${pad};${align}"><${tag} style="line-height:1.2">${String(
          p.text ?? ''
        )}</${tag}></div>`
      );
    }
    case 'Texte':
      return wrap(
        `<div style="padding:12px 32px;font-size:15px;line-height:1.7;color:#374151;${pad};${align}">${
          p.text != null ? String(p.text).replace(/\n/g, '<br>') : ''
        }</div>`
      );

    case 'Image': {
      const h = (p.height as string) || '200';
      const fit = (p.objectFit as string) || 'cover';
      return wrap(
        p.src
          ? `<div style="overflow:hidden"><img src="${esc(p.src)}" alt="${esc(
              p.alt
            )}" style="width:100%;height:${h}px;object-fit:${fit};display:block"></div>`
          : `<div style="height:${h}px;background:#f0efe9;display:flex;align-items:center;justify-content:center;font-size:32px">🖼</div>`
      );
    }

    case 'Bouton': {
      const styles: Record<string, string> = {
        primary: 'background:#2563eb;color:#fff',
        secondary:
          'background:transparent;border:2px solid #2563eb;color:#2563eb',
        ghost: 'background:#f0efe9;color:#1a1917',
      };
      return wrap(
        `<div style="padding:16px 32px;${align};${pad}"><a href="${esc(
          p.url
        )}" style="display:inline-block;padding:12px 28px;border-radius:8px;font-weight:600;font-size:14px;text-decoration:none;${
          styles[p.style as string] || styles.primary
        }">${esc(p.label)}</a></div>`
      );
    }

    case 'Separateur': {
      const rawHeight = Number(p.height);
      const height =
        Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 1;
      return wrap(
        `<div style="padding:8px 32px"><hr style="border:none;border-top:${height}px solid #e2e1d8"></div>`
      );
    }

    case 'Espaceur':
      return wrap(`<div style="height:${Number(p.height) || 0}px"></div>`);

    case 'Section':
      return wrap(
        `<div style="padding:32px"><div style="background:${
          String(p.bg) || '#f8f8f8'
        };border-radius:8px;padding:24px">${
          String(p.content) || ''
        }</div></div>`
      );

    case 'Cta':
      return `<div style="padding:48px 32px;text-align:center${
        p.backgroundColor ? `;background:${String(p.backgroundColor)}` : ''
      }"><h2 style="margin-bottom:8px">${esc(
        p.title
      )}</h2><p style="font-size:15px;color:#6b7280;margin-bottom:24px">${esc(
        p.subtitle
      )}</p><a href="${esc(
        p.btnUrl
      )}" style="display:inline-block;padding:14px 36px;border-radius:10px;background:#2563eb;color:#fff;font-weight:700;font-size:15px;text-decoration:none">${esc(
        p.btnLabel
      )}</a></div>`;

    case 'Libre':
      return `<div style="padding:16px 32px">${String(p.html) || ''}</div>`;

    case 'Formulaire': {
      const fields =
        (p.fields as Array<{
          label: string;
          type: string;
          options?: string[];
        }>) || [];
      const fieldHtml = fields
        .map((f) => {
          let input = '';
          if (f.type === 'textarea')
            input = `<textarea rows="3" style="width:100%;padding:9px 12px;border:1px solid #e2e1d8;border-radius:8px;font-size:13px;font-family:inherit"></textarea>`;
          else if (f.type === 'radio')
            input = `<div style="display:flex;flex-direction:column;gap:6px;margin-top:4px">${(
              f.options ?? []
            )
              .map(
                (o, i) =>
                  `<label style="display:flex;align-items:center;gap:8px;font-size:13px"><input type="radio" name="r${Math.random()}" ${
                    i === 0 ? 'checked' : ''
                  }>${esc(o)}</label>`
              )
              .join('')}</div>`;
          else if (f.type === 'checkbox')
            input = `<div style="display:flex;flex-direction:column;gap:6px;margin-top:4px">${(
              f.options ?? []
            )
              .map(
                (o, i) =>
                  `<label style="display:flex;align-items:center;gap:8px;font-size:13px"><input type="checkbox" ${
                    i === 0 ? 'checked' : ''
                  }>${esc(o)}</label>`
              )
              .join('')}</div>`;
          else if (f.type === 'select')
            input = `<select style="width:100%;padding:9px 12px;border:1px solid #e2e1d8;border-radius:8px;font-size:13px;font-family:inherit">${(
              f.options ?? []
            )
              .map(
                (o, i) =>
                  `<option ${i === 0 ? 'selected' : ''}>${esc(o)}</option>`
              )
              .join('')}</select>`;
          else
            input = `<input type="${f.type}" placeholder="${esc(
              f.label
            )}" style="width:100%;padding:9px 12px;border:1px solid #e2e1d8;border-radius:8px;font-size:13px;font-family:inherit">`;
          return `<div style="margin-bottom:14px"><label style="display:block;font-size:12px;font-weight:600;margin-bottom:5px">${esc(
            f.label
          )}</label>${input}</div>`;
        })
        .join('');
      return wrap(
        `<div style="padding:24px 32px${pad ? ';' + pad : ''}">${
          p.title
            ? `<h3 style="margin-bottom:16px;font-size:16px;font-weight:700">${esc(
                p.title as string
              )}</h3>`
            : ''
        }${fieldHtml}<button style="padding:10px 24px;background:#2563eb;color:#fff;border:none;border-radius:8px;font-weight:600;font-family:inherit">${esc(
          p.submitLabel
        )}</button></div>`
      );
    }

    case 'Pricing': {
      const lines = (p.lines as Array<{ label: string; price: string }>) ?? [];
      const rows = lines
        .map(
          (l) =>
            `<tr><td style="padding:10px 12px;border-bottom:1px solid #e2e1d8;font-size:13px">${esc(
              l.label
            )}</td><td style="padding:10px 12px;border-bottom:1px solid #e2e1d8;font-size:13px;text-align:right;font-weight:600">${esc(
              l.price
            )}</td></tr>`
        )
        .join('');
      return wrap(
        `<div style="padding:24px 32px"><div style="font-weight:700;font-size:16px;margin-bottom:16px">${esc(
          p.title as string
        )}</div><table style="width:100%;border-collapse:collapse">${rows}<tr><td style="padding:14px 12px;font-weight:700;border-top:2px solid #cccbc0">Total</td><td style="padding:14px 12px;font-weight:700;text-align:right;border-top:2px solid #cccbc0">${esc(
          p.total as string
        )}</td></tr></table></div>`
      );
    }

    case 'Offre': {
      const inc = (p.included as string[]) || [];
      const avLabel =
        {
          available: 'Disponible',
          limited: 'Places limitées',
          sold: 'Complet',
        }[p.availability as string] ?? 'Disponible';
      const avColor =
        { available: '#16a34a', limited: '#d97706', sold: '#dc2626' }[
          p.availability as string
        ] ?? '#16a34a';
      const inclHtml = inc
        .map(
          (i) =>
            `<li style="font-size:12px;color:#374151;padding:3px 0 3px 20px;position:relative;list-style:none"><span style="position:absolute;left:0;color:#0d9488;font-weight:700">✓</span>${esc(
              i
            )}</li>`
        )
        .join('');
      return `<div style="margin:8px 16px;border:1px solid #e2e1d8;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.08)"> ${
        p.imageEmoji
          ? `<div style="height:160px;background:linear-gradient(135deg,#e0f2fe,#dbeafe);display:flex;align-items:center;justify-content:center;position:relative"><span style="font-size:48px">${esc(
              p.imageEmoji as string
            )}</span>${
              p.badge && (p.imageEmoji || p.imageSrc)
                ? `<div style="position:absolute;top:12px;right:12px;background:#2563eb;color:#fff;font-size:10px;font-weight:700;padding:3px 10px;border-radius:20px">${esc(
                    p.badge as string
                  )}</div>`
                : ''
            }</div>`
          : ''
      }<div style="padding:20px 24px"><span style="display:inline-block;font-size:11px;padding:2px 10px;border-radius:20px;font-weight:600;background:${avColor}18;color:${avColor};margin-bottom:8px">${avLabel}</span><div style="font-size:18px;font-weight:700;margin-bottom:6px">${esc(
        p.title as string
      )}</div><div style="font-size:13px;color:#6b7280;line-height:1.6;margin-bottom:16px">${esc(
        p.description as string
      )}</div><div style="font-size:28px;font-weight:800;color:#2563eb">${esc(
        p.price as string
      )} ${esc(
        p.currency as string
      )} <span style="font-size:14px;font-weight:400;color:#8a8880">HT</span></div>${
        inclHtml ? `<ul style="margin:14px 0">${inclHtml}</ul>` : ''
      }<a href="${esc(
        p.ctaUrl as string
      )}" style="display:block;padding:12px;text-align:center;background:#2563eb;color:#fff;font-weight:700;font-size:14px;border-radius:8px;text-decoration:none;margin-top:16px">${esc(
        p.ctaLabel as string
      )}</a></div></div>`;
    }

    case 'ValidationBlock': {
      const lines = (p.lines as Array<{ label: string; val: string }>) ?? [];
      const stColor =
        { pending: '#d97706', validated: '#16a34a', rejected: '#dc2626' }[
          p.status as string
        ] ?? '#d97706';
      const stLabel =
        { pending: 'En attente', validated: 'Validé', rejected: 'Refusé' }[
          p.status as string
        ] ?? 'En attente';
      const vrows = lines
        .map(
          (l) =>
            `<div style="display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #e2e1d8;font-size:13px"><span style="color:#8a8880">${esc(
              l.label
            )}</span><span>${esc(l.val)}</span></div>`
        )
        .join('');
      return `<div style="margin:8px 16px;border:1px solid #e2e1d8;border-radius:12px;padding:28px;background:#fafafa"><div style="font-size:18px;font-weight:700;margin-bottom:20px">${esc(
        p.title as string
      )}</div><div style="display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #e2e1d8;font-size:13px"><span style="color:#8a8880">Société</span><span>${esc(
        p.company as string
      )}</span></div>${vrows}<div style="font-size:20px;font-weight:800;color:#2563eb;padding:16px 0 12px">${esc(
        p.total as string
      )}</div><span style="display:inline-flex;padding:6px 14px;border-radius:20px;font-size:12px;font-weight:600;background:${stColor}18;color:${stColor}">${stLabel}</span>${
        p.message
          ? `<div style="margin-top:16px;padding:12px 16px;background:#eff6ff;border-left:3px solid #2563eb;border-radius:0 8px 8px 0;font-size:13px">${esc(
              p.message as string
            )}</div>`
          : ''
      }<div style="display:block;width:100%;margin-top:20px;padding:13px;text-align:center;background:#2563eb;color:#fff;font-weight:700;font-size:14px;border-radius:8px">${esc(
        p.submitLabel as string
      )}</div></div>`;
    }

    case 'FormFieldInput': {
      const label = esc(p.label ?? 'Champ texte');
      const placeholder = esc(p.placeholder ?? '');
      const defaultValue = esc(p.defaultValue ?? '');
      const required = p.required
        ? ' <span style="color:#dc2626">*</span>'
        : '';
      return `<div style="padding:8px 16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px;color:#374151">${label}${required}</label>
        <input type="text" placeholder="${placeholder}" value="${defaultValue}" style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;color:#374151;background:#fff;box-sizing:border-box">
      </div>`;
    }

    case 'FormFieldTextarea': {
      const label = esc(p.label ?? 'Zone de texte');
      const placeholder = esc(p.placeholder ?? '');
      const defaultValue = esc(p.defaultValue ?? '');
      const required = p.required
        ? ' <span style="color:#dc2626">*</span>'
        : '';
      return `<div style="padding:8px 16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px;color:#374151">${label}${required}</label>
        <textarea rows="3" placeholder="${placeholder}" style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;color:#374151;background:#fff;box-sizing:border-box;resize:vertical;font-family:inherit">${defaultValue}</textarea>
      </div>`;
    }

    case 'FormFieldRadio': {
      const label = esc(p.label ?? 'Bouton radio');
      const required = p.required
        ? ' <span style="color:#dc2626">*</span>'
        : '';
      const options = (p.options as string[]) ?? [];
      const groupName = `radio_${nodeId}`;
      const optionsHtml = options
        .map(
          (opt, i) =>
            `<label style="display:flex;align-items:center;gap:8px;font-size:14px;color:#374151;cursor:pointer">
              <input type="radio" name="${groupName}"${
              i === 0 ? ' checked' : ''
            }>
              ${esc(opt)}
            </label>`
        )
        .join('');
      return `<div style="padding:8px 16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:8px;color:#374151">${label}${required}</label>
        <div style="display:flex;flex-direction:column;gap:6px">${optionsHtml}</div>
      </div>`;
    }

    case 'FormFieldCheckbox': {
      const label = esc(p.label ?? 'Case à cocher');
      const required = p.required
        ? ' <span style="color:#dc2626">*</span>'
        : '';
      const options = (p.options as string[]) ?? [];
      const optionsHtml = options
        .map(
          (opt) =>
            `<label style="display:flex;align-items:center;gap:8px;font-size:14px;color:#374151;cursor:pointer">
              <input type="checkbox">
              ${esc(opt)}
            </label>`
        )
        .join('');
      return `<div style="padding:8px 16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:8px;color:#374151">${label}${required}</label>
        <div style="display:flex;flex-direction:column;gap:6px">${optionsHtml}</div>
      </div>`;
    }

    case 'FormFieldSelect': {
      const label = esc(p.label ?? 'Liste déroulante');
      const placeholder = esc(p.placeholder ?? '— Sélectionner —');
      const required = p.required
        ? ' <span style="color:#dc2626">*</span>'
        : '';
      const options = (p.options as string[]) ?? [];
      const optionsHtml = options
        .map((opt) => `<option>${esc(opt)}</option>`)
        .join('');
      return `<div style="padding:8px 16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px;color:#374151">${label}${required}</label>
        <select style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;color:#374151;background:#fff;box-sizing:border-box;font-family:inherit">
          <option value="">${placeholder}</option>
          ${optionsHtml}
        </select>
      </div>`;
    }

    case 'FormContainer': {
      const title = p.title ? esc(p.title as string) : '';
      const childrenHtml = (node.nodes || [])
        .map((childId) => renderNode(nodes, childId))
        .join('');
      return `<div style="border:1px solid #e5e7eb;border-radius:8px;background:#fff;margin:8px 0">${
        title
          ? `<h3 style="font-size:16px;font-weight:700;margin:16px 16px 4px;color:#111827">${title}</h3>`
          : ''
      }<div style="padding:4px 0">${childrenHtml}</div></div>`;
    }

    default:
      // For ROOT or unknown, render children
      return (node.nodes || [])
        .map((childId) => renderNode(nodes, childId))
        .join('');
  }
}

function buildHeader(
  header: HeaderState,
  activePid: PageId,
  // availablePages?: string[],
  url?: string
): string {
  const logo =
    header.logoType === 'image' && header.logoImageSrc
      ? `<img src="${esc(header.logoImageSrc)}" alt="${esc(
          header.logoText
        )}" style="height:${Math.round(
          parseInt(header.height) * 0.55
        )}px;width:auto;vertical-align:middle">`
      : `<span style="font-weight:800;font-size:18px;letter-spacing:-0.02em;color:${
          header.textColor
        }">${esc(header.logoText)}</span>`;
  const cta = header.showCta
    ? `<a href="${esc(
        header.ctaUrl
      )}" style="margin-left:auto;padding:8px 20px;border-radius:8px;font-size:13px;font-weight:700;background:${
        header.accentColor
      };color:#fff;text-decoration:none">${esc(header.ctaLabel)}</a>`
    : '';
  const shadow = header.shadow ? 'box-shadow:0 1px 4px rgba(0,0,0,.08)' : '';

  // Use dynamic pages if provided, otherwise fall back to PAGES
  const pagesToRender = /* availablePages ?? */ PAGES;

  const tabs = pagesToRender
    .map((pid) => {
      // Get label: use PAGE_LABELS if available, otherwise capitalize the pid
      const label =
        PAGE_LABELS[pid] || pid.charAt(0).toUpperCase() + pid.slice(1);
      return `<a href="${
        url ? `${url}/${pid}` : `${pid}.html`
      }" data-page="${pid}" onclick="switchTab('${pid}', '${
        header.accentColor
      }'); return false;" style="padding:10px 18px;font-size:13px;font-weight:${
        pid === activePid ? '700' : '500'
      };color:${
        pid === activePid ? header.accentColor : '#6b7280'
      };border-bottom:2px solid ${
        pid === activePid ? header.accentColor : 'transparent'
      };text-decoration:none;display:inline-block;cursor:pointer" class="nav-tab${
        pid === activePid ? ' active' : ''
      }">${label}</a>`;
    })
    .join('');
  return `<header style="background:${header.bgColor};${shadow};position:sticky;top:0;z-index:100"><div style="display:flex;align-items:center;padding:0 32px;gap:20px;height:${header.height}px">${logo}<span style="flex:1"></span>${cta}</div></header><nav style="display:flex;background:#f0efe9;border-bottom:1px solid #e2e1d8;padding:0 24px">${tabs}</nav>`;
}

export function exportPageHTML(
  craftJSON: string,
  header: HeaderState,
  pageId: PageId,
  // availablePages?: string[],
  url?: string
): string {
  let nodes: Record<string, NodeData> = {};
  try {
    nodes = JSON.parse(craftJSON) as Record<string, NodeData>;
  } catch {
    console.error('Invalid Craft.js JSON');
  }
  console.log('Parsed nodes:', nodes);

  const bodyHtml = Object.keys(nodes)
    .filter(
      (id) =>
        id !== 'ROOT' &&
        nodes[id] &&
        !Object.entries(nodes).some(
          ([parentId, n]) =>
            parentId !== 'ROOT' &&
            ((n.linkedNodes && Object.values(n.linkedNodes).includes(id)) ||
              n.nodes?.includes(id))
        )
    )
    .map((id) => {
      // Only render top-level nodes (direct children of ROOT)
      // const rootNode = nodes.ROOT;
      // if (!rootNode) return '';
      // if (!rootNode.nodes.includes(id)) return '';
      return renderNode(nodes, id);
    })
    .filter(Boolean)
    .join('\n');

  const headerHtml = buildHeader(header, pageId, /* availablePages, */ url);

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${PAGE_LABELS[pageId]} — ${esc(header.logoText)}</title>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700&family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Plus Jakarta Sans',sans-serif;background:#fff;color:#1a1917}
.page-content{display:none}
.page-content.active{display:block}
</style>
<script>
function switchTab(pageId, accentColor) {
  console.log('Switching to page:', pageId);
  
  // Hide all page contents
  var contents = document.querySelectorAll('.page-content');
  console.log('Found page contents:', contents.length);
  for (var i = 0; i < contents.length; i++) {
    contents[i].classList.remove('active');
  }
  
  // Show selected page content
  var activeContent = document.getElementById('page-' + pageId);
  console.log('Active content element:', activeContent);
  if (activeContent) {
    activeContent.classList.add('active');
    console.log('Activated page:', pageId);
  } else {
    console.error('Page content not found for:', pageId);
  }
  
  // Update tab styles
  var tabs = document.querySelectorAll('.nav-tab');
  console.log('Found tabs:', tabs.length);
  for (var j = 0; j < tabs.length; j++) {
    var tab = tabs[j];
    var isActive = tab.getAttribute('data-page') === pageId;
    tab.style.fontWeight = isActive ? '700' : '500';
    tab.style.color = isActive ? accentColor : '#6b7280';
    tab.style.borderBottom = isActive ? '2px solid ' + accentColor : '2px solid transparent';
    if (isActive) {
      tab.classList.add('active');
    } else {
      tab.classList.remove('active');
    }
  }
  
  return false;
}
</script>
</head>
<body>
${headerHtml}
<div id="page-${pageId}" class="page-content active">
${bodyHtml}
</div>
</body>
</html>`;
}

export function exportAllPagesHTML(
  pagesData: Record<string, string>,
  header: HeaderState,
  defaultPageId: PageId,
  labels?: Record<string, string>
): string {
  const availablePages = Object.keys(pagesData);
  const getLabel = (pid: string): string =>
    labels?.[pid] ??
    PAGE_LABELS[pid] ??
    pid.charAt(0).toUpperCase() + pid.slice(1);

  // Generate header with all tabs
  const logo =
    header.logoType === 'image' && header.logoImageSrc
      ? `<img src="${esc(header.logoImageSrc)}" alt="${esc(
          header.logoText
        )}" style="height:${Math.round(
          parseInt(header.height) * 0.55
        )}px;width:auto;vertical-align:middle;object-fit:${
          header.logoObjectFit || 'contain'
        }">`
      : `<span style="font-weight:${header.fontWeight || '800'};font-size:${
          header.fontSize || '18'
        }px;font-family:${
          header.fontFamily || 'inherit'
        };letter-spacing:-0.02em;color:${header.textColor}">${esc(
          header.logoText
        )}</span>`;

  const cta = header.showCta
    ? `<a href="${esc(
        header.ctaUrl
      )}" style="margin-left:auto;padding:8px 20px;border-radius:8px;font-size:13px;font-weight:700;background:${
        header.accentColor
      };color:#fff;text-decoration:none">${esc(header.ctaLabel)}</a>`
    : '';

  const shadow = header.shadow ? 'box-shadow:0 1px 4px rgba(0,0,0,.08)' : '';

  // Generate background styling with image support
  const backgroundStyle = header.bgImageSrc
    ? `background:linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.1)), url(${
        header.bgImageSrc
      });background-size:${header.bgSize || 'cover'};background-position:${
        header.bgPosition || 'center'
      };background-repeat:${header.bgRepeat || 'no-repeat'}`
    : `background:${header.bgColor}`;

  const tabs = availablePages
    .map((pid) => {
      const label = getLabel(pid);
      return `<a href="#" data-page="${pid}" onclick="switchTab('${pid}', '${
        header.accentColor
      }'); return false;" style="padding:10px 18px;font-size:13px;font-weight:${
        pid === defaultPageId ? '700' : '500'
      };color:${
        pid === defaultPageId ? header.accentColor : '#6b7280'
      };border-bottom:2px solid ${
        pid === defaultPageId ? header.accentColor : 'transparent'
      };text-decoration:none;display:inline-block;cursor:pointer" class="nav-tab${
        pid === defaultPageId ? ' active' : ''
      }">${label}</a>`;
    })
    .join('');

  const headerHtml = `<header style="${backgroundStyle};${shadow};z-index:100"><div style="display:flex;align-items:center;padding:0 32px;gap:20px;height:${header.height}px">${logo}<span style="flex:1"></span>${cta}</div></header><nav id="nav-tabs" style="display:flex;background:#f0efe9;border-bottom:1px solid #e2e1d8;padding:0 24px;z-index:99;transition:all 0.3s ease">${tabs}</nav>`;

  // Generate content for each page
  const pagesHtml = availablePages
    .map((pid) => {
      const craftJSON = pagesData[pid];
      let nodes: Record<string, NodeData> = {};
      try {
        nodes = JSON.parse(craftJSON) as Record<string, NodeData>;
      } catch {
        console.error(`Invalid Craft.js JSON for page: ${pid}`);
        return '';
      }

      const bodyHtml = Object.keys(nodes)
        .filter(
          (id) =>
            id !== 'ROOT' &&
            nodes[id] &&
            !Object.entries(nodes).some(
              ([parentId, n]) =>
                parentId !== 'ROOT' &&
                (Object.values(n.linkedNodes ?? {}).includes(id) ||
                  n.nodes?.includes(id))
            )
        )
        .map((id) => renderNode(nodes, id))
        .filter(Boolean)
        .join('\n');

      return `<div id="page-${pid}" class="page-content${
        pid === defaultPageId ? ' active' : ''
      }">\n${bodyHtml}\n</div>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(header.logoText)}</title>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700&family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Plus Jakarta Sans',sans-serif;background:#fff;color:#1a1917}
.page-content{display:none}
.page-content.active{display:block}
#nav-tabs.sticky{position:fixed;top:0;width:100%;box-shadow:0 2px 8px rgba(0,0,0,0.1)}
</style>
<script>
function switchTab(pageId, accentColor) {
  console.log('Switching to page:', pageId);
  
  var contents = document.querySelectorAll('.page-content');
  console.log('Found page contents:', contents.length);
  for (var i = 0; i < contents.length; i++) {
    contents[i].classList.remove('active');
  }
  
  var activeContent = document.getElementById('page-' + pageId);
  console.log('Active content element:', activeContent);
  if (activeContent) {
    activeContent.classList.add('active');
    console.log('Activated page:', pageId);
  } else {
    console.error('Page content not found for:', pageId);
  }
  
  var tabs = document.querySelectorAll('.nav-tab');
  console.log('Found tabs:', tabs.length);
  for (var j = 0; j < tabs.length; j++) {
    var tab = tabs[j];
    var isActive = tab.getAttribute('data-page') === pageId;
    tab.style.fontWeight = isActive ? '700' : '500';
    tab.style.color = isActive ? accentColor : '#6b7280';
    tab.style.borderBottom = isActive ? '2px solid ' + accentColor : '2px solid transparent';
    if (isActive) {
      tab.classList.add('active');
    } else {
      tab.classList.remove('active');
    }
  }
  
  return false;
}

// Sticky navigation handler
window.addEventListener('scroll', function() {
  var nav = document.getElementById('nav-tabs');
  if (nav) {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop >= 250) {
      nav.classList.add('sticky');
    } else {
      nav.classList.remove('sticky');
    }
  }
});
</script>
</head>
<body>
${headerHtml}
${pagesHtml}
</body>
</html>`;
}

export function downloadHTML(html: string, filename: string): void {
  try {
    console.log('Starting download:', filename);
    console.log('HTML size:', html.length, 'characters');

    const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);

    console.log('Download triggered successfully:', filename);
  } catch (error) {
    console.error('Download failed:', error);
    throw new Error(`Échec du téléchargement du fichier ${filename}`);
  }
}
